import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState
} from "react";
import { useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Select
} from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import MuiAlert from "@material-ui/lab/Alert";

import {
  matchCreativesToPlacements,
  applyOverrides,
  getCreativeType,
  buildPlacementDurations,
  buildSiteServedIds,
  getAdSlotChoices,
  getNextAdSlot,
  reasonForUnmatchedCreative,
  reasonForUnmatchedPlacement
} from "../../../utils/creativeMatching";
import { getDurationOptions } from "../Shared/dimensionUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%"
  },
  intro: {
    fontWeight: 400,
    fontSize: "1.15rem",
    marginBottom: theme.spacing(2)
  },
  groupHeading: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    fontWeight: 500
  },
  tablePaper: {
    boxShadow: "none",
    border: "1px solid rgba(0,0,0,0.12)",
    overflowX: "auto"
  },
  table: {
    tableLayout: "fixed",
    width: "100%"
  },
  placementCell: {
    width: "22%",
    verticalAlign: "top"
  },
  typeCell: {
    width: "16%",
    verticalAlign: "top"
  },
  adCell: {
    width: "14%",
    verticalAlign: "top"
  },
  adSelect: {
    fontSize: "0.8125rem"
  },
  assignedCell: {
    width: "48%",
    verticalAlign: "top"
  },
  placementName: {
    fontWeight: 500,
    wordBreak: "break-word"
  },
  secondaryText: {
    color: theme.palette.text.secondary
  },
  assignedWrap: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.5),
    maxWidth: "100%"
  },
  chip: {
    maxWidth: 220,
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis"
    }
  },
  warningsWrap: {
    marginTop: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  warningList: {
    margin: 0,
    paddingLeft: theme.spacing(3)
  },
  warningItem: {
    wordBreak: "break-word"
  },
  emptyAssignment: {
    color: theme.palette.text.disabled,
    fontStyle: "italic"
  }
}));

const compatibilityLabel = (compatibility) => {
  if (compatibility === "IN_STREAM_VIDEO") return "In-Stream Video";
  if (compatibility === "IN_STREAM_AUDIO") return "In-Stream Audio";
  return "Display";
};

const sortOrderForCompatibility = (compatibility) => {
  if (compatibility === "DISPLAY") return 0;
  if (compatibility === "IN_STREAM_VIDEO") return 1;
  if (compatibility === "IN_STREAM_AUDIO") return 2;
  return 3;
};

const creativeChipLabel = (creative) => {
  if (!creative) return "(missing)";
  if (creative.taxonomy && creative.taxonomy.name) return creative.taxonomy.name;
  if (creative.file && creative.file.name) return creative.file.name;
  if (creative.dcmCreative && creative.dcmCreative.name) {
    return creative.dcmCreative.name;
  }
  return creative.id;
};

export default forwardRef((props, ref) => {
  const {
    matchOverrides,
    setMatchOverrides,
    adOverrides,
    setAdOverrides,
    matchGroups
  } = props;
  const classes = useStyles();
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [addMenuPlacementId, setAddMenuPlacementId] = useState(null);

  const creativesState = useSelector((state) => state.creatives);
  const placementInfoData = useSelector((state) => state.placementInfo);

  const placements = creativesState.assignedPlacements || [];
  // allIds order (not byId insertion order) so the chips list in the same
  // order the asset lists render and the ads submit in.
  const allCreatives = useMemo(
    () => creativesState.allIds.map((id) => creativesState.byId[id]).filter(Boolean),
    [creativesState.allIds, creativesState.byId]
  );

  // Same inputs SmartTrafficStepper feeds the matcher, so the preview shows
  // exactly what will be submitted.
  const siteServedIds = useMemo(
    () => buildSiteServedIds(placementInfoData.byId),
    [placementInfoData.byId]
  );

  const placementDurations = useMemo(
    () => buildPlacementDurations(placementInfoData.byId),
    [placementInfoData.byId]
  );

  const typeGroups = useSelector((state) => state.typeGroups);
  const durationOptions = useMemo(() => getDurationOptions(typeGroups), [
    typeGroups
  ]);

  const autoResult = useMemo(
    () =>
      matchCreativesToPlacements(allCreatives, placements, {
        siteServedIds,
        placementDurations,
        durationOptions
      }),
    [allCreatives, placements, siteServedIds, placementDurations, durationOptions]
  );

  const finalAssignments = useMemo(
    () => applyOverrides(autoResult.matches, matchOverrides),
    [autoResult.matches, matchOverrides]
  );

  useImperativeHandle(ref, () => ({
    // Warnings only — never block the user from proceeding.
    validate() {
      return { hasErrors: false, message: "" };
    }
  }));

  const updateOverrideSet = (bucket, placementId, creativeId, mode) => {
    setMatchOverrides((prev) => {
      const nextBucket = { ...prev[bucket] };
      const current = nextBucket[placementId];
      const nextSet = new Set(current ? Array.from(current) : []);
      if (mode === "add") nextSet.add(creativeId);
      else nextSet.delete(creativeId);
      if (nextSet.size === 0) {
        delete nextBucket[placementId];
      } else {
        nextBucket[placementId] = nextSet;
      }
      return { ...prev, [bucket]: nextBucket };
    });
  };

  const handleRemoveAssignment = (placementId, creativeId) => {
    const wasAutoMatched = (autoResult.matches[placementId] || []).includes(
      creativeId
    );
    if (wasAutoMatched) {
      // Mark as removed; also clear from `added` in case both were set.
      updateOverrideSet("added", placementId, creativeId, "remove");
      updateOverrideSet("removed", placementId, creativeId, "add");
    } else {
      // Was a user-added override — just delete it.
      updateOverrideSet("added", placementId, creativeId, "remove");
    }
  };

  const handleAddAssignment = (placementId, creativeId) => {
    const wasRemoved = !!(
      matchOverrides.removed &&
      matchOverrides.removed[placementId] &&
      matchOverrides.removed[placementId].has &&
      matchOverrides.removed[placementId].has(creativeId)
    );
    if (wasRemoved) {
      // Restoring a previously removed auto-match — just clear the removal.
      updateOverrideSet("removed", placementId, creativeId, "remove");
    } else {
      updateOverrideSet("added", placementId, creativeId, "add");
    }
    setAddMenuAnchor(null);
    setAddMenuPlacementId(null);
  };

  const openAddMenu = (event, placementId) => {
    setAddMenuAnchor(event.currentTarget);
    setAddMenuPlacementId(placementId);
  };

  const closeAddMenu = () => {
    setAddMenuAnchor(null);
    setAddMenuPlacementId(null);
  };

  const candidatesForAddMenu = useMemo(() => {
    if (!addMenuPlacementId) return [];
    const assigned = new Set(finalAssignments[addMenuPlacementId] || []);
    const targetPlacement = placements.find(
      (p) => p.id === addMenuPlacementId
    );
    const expectedType = targetPlacement
      ? targetPlacement.compatibility === "IN_STREAM_VIDEO"
        ? "INSTREAM_VIDEO"
        : targetPlacement.compatibility === "IN_STREAM_AUDIO"
        ? "INSTREAM_AUDIO"
        : "DISPLAY"
      : null;
    return allCreatives
      .filter((c) => (c.creativeRole || "PRIMARY") === "PRIMARY")
      .filter((c) => !assigned.has(c.id))
      .map((c) => ({
        creative: c,
        typeMatches: !expectedType || getCreativeType(c) === expectedType
      }));
  }, [
    addMenuPlacementId,
    finalAssignments,
    allCreatives,
    placements
  ]);

  const sortedPlacements = useMemo(() => {
    return [...placements].sort((a, b) => {
      const ao = sortOrderForCompatibility(a.compatibility);
      const bo = sortOrderForCompatibility(b.compatibility);
      if (ao !== bo) return ao - bo;
      const aw = (a.size && a.size.width) || 0;
      const bw = (b.size && b.size.width) || 0;
      if (aw !== bw) return aw - bw;
      const ah = (a.size && a.size.height) || 0;
      const bh = (b.size && b.size.height) || 0;
      return ah - bh;
    });
  }, [placements]);

  // Recompute unmatched lists from the override-adjusted assignments so the
  // warnings reflect what the user is actually about to submit, not the
  // pre-override auto-match.
  const liveUnmatched = useMemo(() => {
    const assignedCreativeIds = new Set();
    const emptyPlacementIds = [];

    // Site-served placements used to be forced into this list unconditionally,
    // because they could never be matched. They can now take a tracking
    // creative, so they are reported like any other placement — listed only
    // when nothing actually landed on them.
    for (const p of placements) {
      const cids = finalAssignments[p.id] || [];
      if (cids.length === 0) {
        emptyPlacementIds.push({
          id: p.id,
          reason: reasonForUnmatchedPlacement(p, {
            placementDurations,
            siteServedIds
          })
        });
      } else {
        for (const cid of cids) assignedCreativeIds.add(cid);
      }
    }

    const unmatchedCreatives = [];
    const primaries = allCreatives.filter(
      (c) => (c.creativeRole || "PRIMARY") === "PRIMARY"
    );
    for (const c of primaries) {
      if (!assignedCreativeIds.has(c.id)) {
        unmatchedCreatives.push({
          id: c.id,
          reason: reasonForUnmatchedCreative(c, placements, { durationOptions })
        });
      }
    }
    return { unmatchedCreatives, unmatchedPlacements: emptyPlacementIds };
  }, [
    finalAssignments,
    placements,
    allCreatives,
    siteServedIds,
    placementDurations,
    durationOptions
  ]);

  const handleAdChange = (placementId, value) => {
    setAdOverrides((prev) => {
      const next = { ...prev };
      // Slot 1 is the default, so drop the entry rather than storing it —
      // keeps the override object meaning "what the user actually changed".
      if (value === 1) delete next[placementId];
      else next[placementId] = value;
      return next;
    });
  };

  /**
   * Which ad a placement traffics in.
   *
   * Placements of the same type, size and spot length share an ad by default;
   * this is how the user splits them apart. Only ads of the same natural
   * group are offered — CM360 ads are per type and size, so any other move
   * would be unrepresentable.
   */
  const renderAdCell = (placement) => {
    const groups = matchGroups || [];
    const choices = getAdSlotChoices(
      placement,
      groups,
      placementDurations,
      siteServedIds
    );
    const currentSlot = Number((adOverrides || {})[placement.id]) || 1;

    // A placement with no creatives forms no ad, so there is nothing to split.
    if (choices.length === 0) {
      return <span className={classes.emptyAssignment}>—</span>;
    }

    const nextSlot = getNextAdSlot(
      placement,
      groups,
      placementDurations,
      siteServedIds
    );

    return (
      <Select
        value={currentSlot}
        onChange={(e) => handleAdChange(placement.id, Number(e.target.value))}
        className={classes.adSelect}
        inputProps={{ "aria-label": `Ad for placement ${placement.name}` }}
      >
        {choices.map((choice) => (
          <MenuItem key={choice.slot} value={choice.slot}>
            {`Ad ${choice.adNumber}`}
          </MenuItem>
        ))}
        <MenuItem value={nextSlot}>New ad</MenuItem>
      </Select>
    );
  };

  const renderAssignmentCell = (placementId) => {
    const cids = finalAssignments[placementId] || [];
    return (
      <div className={classes.assignedWrap}>
        {cids.length === 0 && (
          <span className={classes.emptyAssignment}>none</span>
        )}
        {cids.map((cid) => {
          const creative = creativesState.byId[cid];
          const label = creativeChipLabel(creative);
          return (
            <Chip
              key={cid}
              label={label}
              title={label}
              size="small"
              className={classes.chip}
              onDelete={() => handleRemoveAssignment(placementId, cid)}
            />
          );
        })}
        <IconButton
          aria-label="add creative"
          size="small"
          onClick={(e) => openAddMenu(e, placementId)}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </div>
    );
  };

  const unmatchedCreativeReasons = liveUnmatched.unmatchedCreatives;
  const unmatchedPlacementReasons = liveUnmatched.unmatchedPlacements;

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.intro}>
        Review the auto-matched assignments. Creatives are paired with
        placements based on type and dimensions. You'll be able to adjust before
        submitting.
      </Typography>

      <Paper className={classes.tablePaper}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.placementCell}>Placement</TableCell>
              <TableCell className={classes.typeCell}>Size / Type</TableCell>
              <TableCell className={classes.adCell}>Ad</TableCell>
              <TableCell className={classes.assignedCell}>
                Assigned creatives
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPlacements.map((p) => (
              <TableRow key={p.id}>
                <TableCell className={classes.placementCell}>
                  <div className={classes.placementName}>{p.name}</div>
                  <Typography variant="caption" className={classes.secondaryText}>
                    ID: {p.id}
                  </Typography>
                </TableCell>
                <TableCell className={classes.typeCell}>
                  <div>{p.size ? `${p.size.width} × ${p.size.height}` : "—"}</div>
                  <Typography variant="caption" className={classes.secondaryText}>
                    {siteServedIds.has(String(p.id))
                      ? "Site-Served"
                      : compatibilityLabel(p.compatibility)}
                  </Typography>
                </TableCell>
                <TableCell className={classes.adCell}>
                  {renderAdCell(p)}
                </TableCell>
                <TableCell className={classes.assignedCell}>
                  {renderAssignmentCell(p.id)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={closeAddMenu}
      >
        {candidatesForAddMenu.length === 0 && (
          <MenuItem disabled>No more creatives available</MenuItem>
        )}
        {candidatesForAddMenu.map(({ creative, typeMatches }) => (
          <MenuItem
            key={creative.id}
            onClick={() => handleAddAssignment(addMenuPlacementId, creative.id)}
          >
            {creativeChipLabel(creative)}
            {!typeMatches && (
              <span style={{ marginLeft: 8, color: "#b26a00", fontSize: "0.85em" }}>
                (type mismatch — will be allowed)
              </span>
            )}
          </MenuItem>
        ))}
      </Menu>

      <div className={classes.warningsWrap}>
        {unmatchedCreativeReasons.length > 0 && (
          <MuiAlert severity="warning">
            <Typography variant="body2">
              {unmatchedCreativeReasons.length} creative
              {unmatchedCreativeReasons.length === 1 ? " " : "s "}
              won't be assigned to any placement:
            </Typography>
            <ul className={classes.warningList}>
              {unmatchedCreativeReasons.map(({ id, reason }) => {
                const c = creativesState.byId[id];
                return (
                  <li key={id} className={classes.warningItem}>
                    <strong>{creativeChipLabel(c)}</strong> — {reason}
                  </li>
                );
              })}
            </ul>
          </MuiAlert>
        )}

        {unmatchedPlacementReasons.length > 0 && (
          <MuiAlert severity="info">
            <Typography variant="body2">
              {unmatchedPlacementReasons.length} placement
              {unmatchedPlacementReasons.length === 1 ? " " : "s "}
              will remain empty:
            </Typography>
            <ul className={classes.warningList}>
              {unmatchedPlacementReasons.map(({ id, reason }) => {
                const p = placements.find((pl) => pl.id === id);
                return (
                  <li key={id} className={classes.warningItem}>
                    <strong>{p ? p.name : id}</strong> — {reason}
                  </li>
                );
              })}
            </ul>
          </MuiAlert>
        )}
      </div>
    </div>
  );
});
